import React, { useState, useEffect, useMemo } from 'react';
import Lottie from 'lottie-react';

const Preloader = ({ 
  message = "loading...4123", 
  className = "",
  size = "medium",
  showMessage = true,
  primaryColor = "#1a3b5c", // Dark blue-green
  secondaryColor = "#cdfed7", // Light green
  speed = 1
}) => {
  const [animationData, setAnimationData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load animation data dynamically (this method works)
  useEffect(() => {
    const loadAnimation = async () => {
      try {
        const data = await import('../../preloader.json');
        setAnimationData(data.default || data);
      } catch (err) {
        console.error('Failed to load animation:', err);
        // Fallback to fetch from public folder
        try {
          const response = await fetch('/preloader.json');
          if (response.ok) {
            const data = await response.json();
            setAnimationData(data);
          }
        } catch (fetchErr) {
          console.error('Fetch fallback failed:', fetchErr);
        }
      } finally {
        setLoading(false);
      }
    };

    loadAnimation();
  }, []);
  const getSizeClasses = () => {
    // The animation has an aspect ratio of 1080x500 (2.16:1), so we need to preserve this
    switch (size) {
      case 'small':
        return 'w-48 h-auto'; // Allow height to scale automatically
      case 'large':
        return 'w-96 h-auto'; // Allow height to scale automatically  
      case 'medium':
      default:
        return 'w-72 h-auto'; // Allow height to scale automatically
    }
  };

  // Function to convert hex to RGB values
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16) / 255,
      g: parseInt(result[2], 16) / 255,
      b: parseInt(result[3], 16) / 255
    } : null;
  };

  // Create a customized animation with new colors (only if animation is loaded)
  const customizedAnimation = useMemo(() => {
    if (!animationData) return null;
    
    // Disable color customization - the JSON already has optimized high-contrast colors
    const enableColorCustomization = false;
    
    if (!enableColorCustomization) {
      return animationData;
    }
    
    try {
      // Deep clone the animation data to avoid mutating the original
      const animation = JSON.parse(JSON.stringify(animationData));
      const primaryRgb = hexToRgb(primaryColor);
      const secondaryRgb = hexToRgb(secondaryColor);
      
      if (primaryRgb && secondaryRgb) {
        // More reliable color customization approach
        const updateColors = (obj, depth = 0) => {
          if (depth > 15) return; // Prevent infinite recursion
          
          if (typeof obj === 'object' && obj !== null) {
            if (Array.isArray(obj)) {
              obj.forEach(item => updateColors(item, depth + 1));
            } else {
              Object.keys(obj).forEach(key => {
                // Look for color properties in shapes
                if (key === 'c' && obj.c && obj.c.k) {
                  try {
                    if (Array.isArray(obj.c.k) && obj.c.k.length >= 3) {
                      // Static color - apply primary color
                      obj.c.k[0] = primaryRgb.r;
                      obj.c.k[1] = primaryRgb.g;
                      obj.c.k[2] = primaryRgb.b;
                    } else if (Array.isArray(obj.c.k)) {
                      // Animated color - apply to all keyframes
                      obj.c.k.forEach((keyframe, index) => {
                        if (keyframe.s && Array.isArray(keyframe.s) && keyframe.s.length >= 3) {
                          // Alternate between primary and secondary colors for variation
                          const color = index % 2 === 0 ? primaryRgb : secondaryRgb;
                          keyframe.s[0] = color.r;
                          keyframe.s[1] = color.g;
                          keyframe.s[2] = color.b;
                        }
                      });
                    }
                  } catch (colorErr) {
                    console.warn('Error updating color property:', colorErr);
                  }
                }
                // Look for stroke colors
                else if (key === 'sc' && obj.sc && obj.sc.k) {
                  try {
                    if (Array.isArray(obj.sc.k) && obj.sc.k.length >= 3) {
                      // Static stroke color - apply secondary color
                      obj.sc.k[0] = secondaryRgb.r;
                      obj.sc.k[1] = secondaryRgb.g;
                      obj.sc.k[2] = secondaryRgb.b;
                    }
                  } catch (colorErr) {
                    console.warn('Error updating stroke color:', colorErr);
                  }
                }
                else {
                  updateColors(obj[key], depth + 1);
                }
              });
            }
          }
        };
        
        updateColors(animation);
      }
      
      return animation;
    } catch (error) {
      console.error('Error customizing animation colors:', error);
      return animationData; // Return original if customization fails
    }
  }, [animationData, primaryColor, secondaryColor]);

  // Show loading state while animation loads
  if (loading || !customizedAnimation) {
    return (
      <div className={`flex flex-col items-center justify-center ${className}`}>
        <div className={`${getSizeClasses()} mb-4 flex items-center justify-center`}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
        {showMessage && (
          <p className="text-white text-lg font-medium animate-pulse">
            {message}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <div className={`${getSizeClasses()} mb-4 flex items-center justify-center`} style={{ aspectRatio: '2.16/1' }}>
        <Lottie
          animationData={customizedAnimation}
          loop={true}
          autoplay={true}
          speed={speed}
          style={{
            width: '100%',
            height: 'auto'
          }}
          rendererSettings={{
            preserveAspectRatio: 'xMidYMid meet'
          }}
        />
      </div>
      {showMessage && (
        <p className="text-white text-lg font-medium animate-pulse">
          {message}
        </p>
      )}
    </div>
  );
};

export default Preloader;
